import React from 'react';
import { ChevronRight } from 'lucide-react';

import { formatResizeDisplay, formatTokenCount } from '../../utils/formatters';

// The Figma design omits this run metadata entirely. We keep the data (it's
// useful for debugging model runs) but tuck it behind a quiet "Run details"
// disclosure with uniform subtle chips so it doesn't compete with the clean
// summary stat row.
const AnalysisResultMetaBadges = ({
  analysisResult,
  modelName,
  useEbayRag,
  useNotesRag,
  assumeHiddenItems,
}) => {
  const analysisUsage = (analysisResult && typeof analysisResult.usage === 'object') ? analysisResult.usage : {};
  const inputTokens = analysisUsage.prompt_tokens ?? analysisUsage.input_tokens ?? null;
  const outputTokens = analysisUsage.completion_tokens ?? analysisUsage.output_tokens ?? null;
  const totalTokens = analysisUsage.total_tokens ?? (
    Number.isFinite(Number(inputTokens)) && Number.isFinite(Number(outputTokens))
      ? (Number(inputTokens) + Number(outputTokens))
      : null
  );
  const cacheHit = (
    analysisResult?.cache_hit === true ||
    String(analysisResult?.analysis_source || '').toLowerCase() === 'cache'
  );
  const hasDuration = (
    (analysisResult.analysis_duration_ms !== undefined && analysisResult.analysis_duration_ms !== null) ||
    (analysisResult.duration_ms !== undefined && analysisResult.duration_ms !== null)
  );
  const hasCost = (
    analysisResult.analysis_cost_display !== undefined ||
    analysisResult.cost_display !== undefined ||
    analysisResult.analysis_cost !== undefined ||
    analysisResult.cost !== undefined
  );
  const costText = (
    analysisResult.analysis_cost_display ||
    analysisResult.cost_display ||
    ((analysisResult.analysis_cost !== undefined && analysisResult.analysis_cost !== null)
      ? `$${Number(analysisResult.analysis_cost).toFixed(4)}`
      : ((analysisResult.cost !== undefined && analysisResult.cost !== null)
        ? `$${Number(analysisResult.cost).toFixed(4)}`
        : 'N/A'))
  );
  const ebayRagOn = analysisResult.use_ebay_rag !== undefined ? Boolean(analysisResult.use_ebay_rag) : useEbayRag;
  const notesRagOn = analysisResult.use_notes_rag !== undefined ? Boolean(analysisResult.use_notes_rag) : useNotesRag;
  const hiddenOn = analysisResult.assume_hidden_items !== undefined
    ? Boolean(analysisResult.assume_hidden_items)
    : assumeHiddenItems;

  const chip = 'bg-surface-2 text-muted px-2 py-1 rounded';

  return (
    <details className="mb-3 sm:mb-4 group">
      <summary className="cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden inline-flex items-center gap-1.5 text-xs text-muted hover:text-ink transition-colors">
        Run details
        <ChevronRight size={14} className="transition-transform group-open:rotate-90" />
      </summary>
      <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-mono">
        <span className={chip}>Model: {analysisResult.model || modelName || 'N/A'}</span>
        <span className={chip}>Resize: {formatResizeDisplay(analysisResult.resize_mode)}</span>
        <span className={chip}>Compression: {analysisResult.compression_mode || 'N/A'}</span>
        <span className={chip}>
          eBay RAG: {ebayRagOn ? `on (${Number(analysisResult.ebay_rag_result_count || 0)} refs)` : 'off'}
        </span>
        <span className={chip}>
          Notes RAG: {notesRagOn ? `on (${Number(analysisResult.notes_rag_example_count || 0)} examples)` : 'off'}
        </span>
        <span className={chip}>Hidden Items: {hiddenOn ? 'on' : 'off'}</span>
        {hasDuration && (
          <span className={chip}>
            Speed: {Number(analysisResult.analysis_duration_ms || analysisResult.duration_ms).toFixed(0)}ms
          </span>
        )}
        {hasCost && <span className={chip}>Cost: {costText}</span>}
        <span className={chip}>Src: {cacheHit ? 'CACHE' : 'LIVE'}</span>
        <span className={chip}>In Tok: {formatTokenCount(inputTokens)}</span>
        <span className={chip}>Out Tok: {formatTokenCount(outputTokens)}</span>
        <span className={chip}>Tot Tok: {formatTokenCount(totalTokens)}</span>
      </div>
    </details>
  );
};

export default AnalysisResultMetaBadges;
